/**
 * Aba Ambientes: busca + filtros por status, e cada pedido como card
 * recolhível com a identidade (lotes, vendedor, revisões, datas, prazos,
 * valor) e a tabela de ambientes (5 colunas; 8 quando o card é largo).
 * Clicar numa linha abre o painel do ambiente.
 */
import React from "react";
import { FiChevronDown, FiChevronRight, FiMessageSquare, FiSearch, FiX } from "react-icons/fi";

import { formatarData } from "../../../../utils/calculos";
import { formatarReaisBR, plural } from "../../../../utils/dinheiro";
import Recolhivel from "../comum/Recolhivel";
import { diferencaDaReprogramacao, pedidoExtra, resumoDasRevisoes, statusCurto, tomDoStatus } from "../dados/ficha";
import { SEM_VALOR } from "../dados/montarClientes";

const OCULTO = "••••••";

function dataCurta(valor) {
  const texto = formatarData(valor);
  return texto === "—" ? "—" : texto.slice(0, 5);
}

function PilulasDeStatus({ ambiente }) {
  const todas = [{ status: ambiente.status, statusVariant: ambiente.statusVariant }, ...(ambiente.pendencias || [])];
  return (
    <span className="cl-a-st">
      {todas.map((s) => (
        <span key={s.status} className={`cl-pill sm p-${tomDoStatus(s.status, s.statusVariant)}`} title={s.status}>
          <span>{statusCurto(s.status)}</span>
        </span>
      ))}
    </span>
  );
}

function Diferenca({ dias }) {
  if (dias == null) return null;
  const atrasou = dias > 0;
  return (
    <span className={`cl-dlt ${atrasou ? "tarde" : "cedo"}`} title={atrasou ? `${dias} dias depois do previsto` : `${-dias} dias antes do previsto`}>
      {atrasou ? `+${dias}d` : `−${-dias}d`}
    </span>
  );
}

function LinhaDoAmbiente({ ambiente, selecionado, comentarios, precosVisiveis, aoAbrir }) {
  const dif = diferencaDaReprogramacao(ambiente);
  const marceneiro = ambiente.marceneiro === SEM_VALOR ? "—" : ambiente.marceneiro;
  return (
    <button type="button" className="cl-ar" aria-current={selecionado || undefined} data-ambiente={ambiente.id}
      aria-label={`${ambiente.ambiente}, ${ambiente.status}, lote ${ambiente.lote}`} onClick={(e) => aoAbrir(ambiente, e.currentTarget)}>
      <span className="cl-an">
        <span title={ambiente.ambiente}>{ambiente.ambiente}</span>
        {comentarios > 0 && (
          <span className="cl-cm" title={plural(comentarios, "comentário", "comentários")}>
            <FiMessageSquare size={11} aria-hidden="true" />{comentarios}
          </span>
        )}
      </span>
      <span className="cl-a-x">{ambiente.lote}</span>
      <PilulasDeStatus ambiente={ambiente} />
      <span className="cl-a-x xl">{ambiente.prazo}</span>
      <span className="cl-a-x">{dataCurta(ambiente.entregaContrato)}</span>
      <span className="cl-a-x">{dataCurta(ambiente.dataReprogramada)}<Diferenca dias={dif} /></span>
      <span className="cl-a-x xl sans" title={marceneiro}>{marceneiro}</span>
      <span className={`cl-a-x xl${precosVisiveis ? "" : " priv"}`}>{precosVisiveis ? formatarReaisBR(ambiente.valor) : OCULTO}</span>
      <span className="cl-a-x cl-go" aria-hidden="true"><FiChevronRight size={14} /></span>
      <span className="cl-a-m">
        <span>Lote <b>{ambiente.lote}</b></span>
        <span>prev. <b>{dataCurta(ambiente.entregaContrato)}</b></span>
        {ambiente.dataReprogramada && <span>reprog. <b>{dataCurta(ambiente.dataReprogramada)}</b><Diferenca dias={dif} /></span>}
        {marceneiro !== "—" && <span>{marceneiro}</span>}
      </span>
    </button>
  );
}

function Identidade({ pedido, precosVisiveis }) {
  const revisoes = resumoDasRevisoes(pedido.revisoes);
  return (
    <div className="cl-idg">
      <div className="cl-idc">
        <span>Lotes</span>
        <span className="cl-lots">
          {pedido.lotes.length ? pedido.lotes.map((l) => <span key={l} className="cl-lot">{l}</span>) : <b>—</b>}
        </span>
      </div>
      <div className="cl-idc"><span>Vendedor · Consultor</span>
        <b className="sans">{[pedido.vendedores.join(", ") || "—", pedido.consultores.join(", ") || "—"].join(" · ")}</b></div>
      <div className="cl-idc"><span>Revisões</span><b>{revisoes.texto}</b>{revisoes.ultima && <small>última em {revisoes.ultima}</small>}</div>
      <div className="cl-idc"><span>Data da venda</span><b>{pedido.dataVendaLabel}</b></div>
      <div className="cl-idc"><span>Entrega prevista</span><b>{pedido.entregaContratoLabel}</b></div>
      <div className="cl-idc"><span>Reprogramada</span><b>{pedido.reprogramacaoLabel}</b></div>
      <div className="cl-idc"><span>Prazos em dias</span><b>{pedido.prazos.join(", ") || "—"}</b></div>
      <div className="cl-idc"><span>Valor do pedido</span>
        <b className={precosVisiveis ? undefined : "priv"}>{precosVisiveis ? formatarReaisBR(pedido.valorTotal) : OCULTO}</b></div>
    </div>
  );
}

function CardDoPedido({ pedido, ambientes, aberto, aoAlternar, ambienteAberto, comentariosDe, precosVisiveis, aoAbrirAmbiente }) {
  const idCorpo = `pedido-corpo-${pedido.id}`;
  return (
    <section className="cl-pcard" id={`pedido-${pedido.id}`} aria-label={`Pedido ${pedido.numero || pedido.label}`}>
      <button type="button" className="cl-pcard-h" aria-expanded={aberto} aria-controls={idCorpo} onClick={aoAlternar}>
        <span style={{ minWidth: 0 }}>
          <span className="cl-pc-t">
            Pedido {pedido.numero || pedido.label}
            {pedidoExtra(pedido) && <span className="cl-pill sm p-vio"><span>Extra</span></span>}
          </span>
          <span className="cl-pc-s">
            {plural(pedido.totalAmbientes, "ambiente", "ambientes")} · {pedido.totalEmAndamento} em andamento · {pedido.totalEntregues} entregues
            {pedido.totalDelayDays > 0 && <> · <span className="cl-r">{plural(pedido.totalDelayDays, "dia", "dias")} de atraso</span></>}
          </span>
          <span className="cl-pc-chips">
            {pedido.statusCounts.map((s) => (
              <span key={s.label} className={`cl-pill sm p-${tomDoStatus(s.label, s.tone)}`} title={s.label}>
                <span>{s.count} {s.label === "Entregue" ? "entregue" : statusCurto(s.label)}</span>
              </span>
            ))}
          </span>
        </span>
        <span className="cl-chev" aria-hidden="true"><FiChevronDown size={16} /></span>
      </button>
      <Recolhivel aberto={aberto} id={idCorpo}>
        <div className="cl-pc-body">
          <Identidade pedido={pedido} precosVisiveis={precosVisiveis} />
          <div className="cl-atbl" role="list" aria-label={`Ambientes do pedido ${pedido.numero || pedido.label}`}>
            <div className="cl-ah" aria-hidden="true">
              <span>Ambiente</span><span>Lote</span><span>Status</span><span className="xl">Prazo</span>
              <span>Entrega prev.</span><span>Reprogramada</span><span className="xl">Marceneiro</span><span className="xl">Valor</span><span />
            </div>
            {ambientes.map((a) => (
              <LinhaDoAmbiente key={a.id} ambiente={a} selecionado={ambienteAberto === a.id}
                comentarios={comentariosDe(a.row)} precosVisiveis={precosVisiveis} aoAbrir={aoAbrirAmbiente} />
            ))}
            {ambientes.length === 0 && <div className="cl-empty" style={{ padding: 24 }}>Nenhum ambiente com esse filtro.</div>}
          </div>
        </div>
      </Recolhivel>
    </section>
  );
}

export default function AbaAmbientes({
  pedidos, filtros, busca, aoBuscar, status, aoFiltrar, abertos, aoAlternarPedido, ambienteAberto,
  comentariosDe, precosVisiveis, aoAbrirAmbiente, campoRef,
}) {
  const total = pedidos.reduce((s, p) => s + p.totalAmbientes, 0);
  return (
    <>
      <div className="cl-tools">
        <label className="cl-search">
          <FiSearch size={16} aria-hidden="true" />
          <input ref={campoRef} type="search" aria-label="Buscar ambiente, lote ou marceneiro" placeholder="Ambiente, lote, marceneiro…"
            value={busca} onChange={(e) => aoBuscar(e.target.value)} />
          {busca ? (
            <button type="button" className="cl-limpar" aria-label="Limpar busca" onClick={() => aoBuscar("")}><FiX size={14} /></button>
          ) : <kbd aria-hidden="true">/</kbd>}
        </label>
        <div className="cl-filters cl-hscroll" role="group" aria-label="Filtrar por status" style={{ flex: "1 1 auto" }}>
          <button type="button" className="cl-chip" aria-pressed={!status} onClick={() => aoFiltrar("")}>Todos <b>{total}</b></button>
          {filtros.map((f) => (
            <button key={f.id} type="button" className="cl-chip" aria-pressed={status === f.id}
              onClick={() => aoFiltrar(status === f.id ? "" : f.id)}>
              {f.rotulo} <b>{f.total}</b>
            </button>
          ))}
        </div>
      </div>
      {pedidos.map((p) => (
        <CardDoPedido key={p.id} pedido={p} ambientes={p.ambientesFiltrados} aberto={Boolean(abertos[p.id])}
          aoAlternar={() => aoAlternarPedido(p.id)} ambienteAberto={ambienteAberto} comentariosDe={comentariosDe}
          precosVisiveis={precosVisiveis} aoAbrirAmbiente={aoAbrirAmbiente} />
      ))}
    </>
  );
}
